import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import ImmersiveLayout from "../components/ImmersiveLayout";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Immersive About — uses /cms/about-v2 with hero, sections, team, quote.
 * Falls back gracefully to default content if CMS unreachable.
 */
export default function AboutPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const id = language === "id";

  useEffect(() => {
    axios.get(`${API}/cms/about-v2`).then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) {
    return (
      <ImmersiveLayout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="font-heading text-2xl text-seikat-bone/60 italic">{id ? "Memuat cerita..." : "Loading story..."}</p>
        </div>
      </ImmersiveLayout>
    );
  }

  const hero = data.hero || {};
  const sections = data.sections || [];
  const team = data.team || [];
  const quote = data.quote || {};

  return (
    <ImmersiveLayout>
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end pb-32 px-6 lg:px-16">
        <div className="absolute inset-0">
          {hero.media_url && <img src={hero.media_url} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-seikat-ink/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink via-seikat-ink/30 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-7 max-w-4xl"
          >
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">{hero.eyebrow || "STORY"}</span>
            </div>
            <h1
              className="font-heading text-5xl sm:text-6xl lg:text-8xl text-seikat-bone leading-[0.95] tracking-tight"
              dangerouslySetInnerHTML={{ __html: id ? hero.headline_id : hero.headline_en }}
              data-testid="about-hero-title"
            />
          </motion.div>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="relative py-24 px-6 lg:px-16">
        <div className="max-w-7xl mx-auto space-y-32">
          {sections.map((s, i) => (
            <motion.div
              key={s.id || i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={`grid lg:grid-cols-12 gap-12 lg:gap-20 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
              data-testid={`about-section-${i}`}
            >
              <div className="lg:col-span-5">
                {s.image_url && (
                  <div className="relative aspect-[4/5] overflow-hidden rounded-lg">
                    <img src={s.image_url} alt={s.title_en} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.2s]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink/40 via-transparent to-transparent" />
                  </div>
                )}
              </div>
              <div className="lg:col-span-7 space-y-6">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-px bg-seikat-lime" />
                  <span className="eyebrow-immersive">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h2 className="font-heading text-4xl sm:text-5xl text-seikat-bone leading-[1.05]">
                  {id ? s.title_id : s.title_en}
                </h2>
                <p className="font-body text-seikat-bone/75 text-base lg:text-lg leading-relaxed font-light whitespace-pre-line">
                  {id ? s.body_id : s.body_en}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* QUOTE */}
      {quote.text_id && (
        <section className="relative py-32 px-6 lg:px-16">
          <div className="max-w-5xl mx-auto text-center">
            <motion.blockquote
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl text-seikat-bone leading-[1.2] italic"
            >
              "{id ? quote.text_id : quote.text_en}"
            </motion.blockquote>
            <p className="font-body text-seikat-bone-muted text-xs tracking-[0.3em] uppercase mt-8">{quote.attribution}</p>
          </div>
        </section>
      )}

      {/* TEAM */}
      {team.length > 0 && (
        <section className="relative py-24 px-6 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-14">
              <span className="w-8 h-px bg-seikat-pink" />
              <span className="eyebrow-immersive">{id ? "Tim Atelier" : "The Atelier"}</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {team.map((m, i) => (
                <motion.div
                  key={m.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className="group"
                  data-testid={`team-card-${i}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden rounded-lg mb-5">
                    <img src={m.image_url} alt={m.name} className="w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-seikat-ink/70 via-transparent to-transparent" />
                  </div>
                  <h3 className="font-heading text-2xl text-seikat-bone">{m.name}</h3>
                  <p className="font-body text-seikat-pink text-xs tracking-[0.2em] uppercase mt-1">{id ? m.role_id : m.role_en}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative py-28 px-6 lg:px-16">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="font-heading text-4xl sm:text-5xl text-seikat-bone leading-tight">
            {id ? <>Rangkai <em>kenanganmu</em> sendiri.</> : <>Weave your own <em>memory</em>.</>}
          </h2>
          <button onClick={() => navigate("/memory-bouquet/create")} className="btn-pill btn-pill--solid" data-magnet data-testid="about-cta">
            {id ? "Mulai Memory Bouquet" : "Begin Memory Bouquet"} <ArrowRight size={14} />
          </button>
        </div>
      </section>

      <footer className="border-t border-white/10 mt-16 py-12 px-6 lg:px-16 pb-32">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
          <div className="font-heading text-3xl text-seikat-bone">Seikat<span className="text-seikat-pink">.</span></div>
          <div className="font-body text-xs tracking-widest text-seikat-bone-muted uppercase">© 2026 · Jakarta, Indonesia</div>
        </div>
      </footer>
    </ImmersiveLayout>
  );
}
